import React, { useState, useEffect } from "react";
import API from "../services/api";
import { apiBaseUrl } from "../services/native";
import { useCanEdit, ReadOnlyBanner } from "../services/permissions";
import { ArrowLeft, Package, Phone, MapPin, FileText, Image as ImageIcon, Mail, CheckCircle, Circle, DollarSign, Clock, Edit3, Save, X, Plus, Trash2, Truck, RefreshCw } from "lucide-react";
import { format } from "date-fns";

export default function OrderDetail({ order: initialOrder, onBack }) {
  const canEdit = useCanEdit();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState([]);
  const [assigningRun, setAssigningRun] = useState(false);

  const fetchOrderDetails = async () => {
    if (!initialOrder?.id) return;
    try {
      const r = await API.get(`/jobs/${encodeURIComponent(initialOrder.id)}`);
      setDetails(r.data);
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
    fetchRuns();
  }, [initialOrder?.id]);

  const fetchRuns = async () => {
    try {
      // no trailing slash - FastAPI 307-redirects to localhost through nginx
      const res = await API.get('/runs');
      setRuns(res.data.filter(r => r.status !== 'completed'));
    } catch (e) {
      console.error(e);
    }
  };

  const data = details || initialOrder;
  const cleanText = (text) => {
    if (!text) return "";
    return text.replace(/\|/g, "").replace(/\s{2,}/g, " ").replace(/^-s*/, "").trim();
  };
  
  const [editRequested, setIsEditing] = useState(false);
  // A reader is never in edit mode, whatever the state says. Every editable
  // field on this screen hangs off isEditing, so deriving it here gates the
  // whole form at once rather than relying on the Edit button being hidden.
  const isEditing = canEdit && editRequested;
  const [editComingFrom, setEditComingFrom] = useState("");
  const [editDestination, setEditDestination] = useState("");
  const [editBillTo, setEditBillTo] = useState("");
  const [editProducts, setEditProducts] = useState([]);
  const [editInvoiceNo, setEditInvoiceNo] = useState([""]);
  const [editPoNumber, setEditPoNumber] = useState([""]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [isReparsing, setIsReparsing] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);

  const handleReparse = async () => {
    setIsReparsing(true);
    try {
      await API.post(`/jobs/${encodeURIComponent(data.id)}/reparse`);
      await fetchOrderDetails();
      alert("Order successfully re-extracted!");
    } catch (err) {
      alert("Failed to re-extract order: " + (err.response?.data?.detail || err.message));
    } finally {
      setIsReparsing(false);
    }
  };

  // Re-run the deterministic pipeline (re-OCRs the attachments). Use after a
  // clearer scan has been re-sent for a low-confidence order.
  const handleReprocess = async () => {
    setIsReprocessing(true);
    try {
      const res = await API.post(`/jobs/${encodeURIComponent(data.id)}/reprocess?ocr=true`);
      await fetchOrderDetails();
      const outcome = res.data?.result;
      if (outcome === "improved") alert("Reprocessed - order details updated.");
      else if (outcome === "ocr_exhausted") alert("Reprocessed, but the attachment is an unreadable scan. Please re-send a clearer copy or enter the details manually.");
      else alert("Reprocessed - no new details could be extracted.");
    } catch (err) {
      alert("Failed to reprocess: " + (err.response?.data?.detail || err.message));
    } finally {
      setIsReprocessing(false);
    }
  };

  const handleSendConfirmation = async () => {
    // This sends a REAL email to the store that requested the job.
    if (!window.confirm("Send a booking confirmation email to the requesting store?")) return;
    setSendingEmail(true);
    try {
      const res = await API.post(`/jobs/${encodeURIComponent(data.id)}/send-confirmation`);
      // Branch on the payload - success:false carries the reason nothing sent.
      if (res?.data?.sent) {
        alert(res.data.message || "Confirmation sent.");
      } else {
        alert(res?.data?.message || "Nothing was sent.");
      }
    } catch (err) {
      alert("Failed to send email confirmation: " + (err.response?.data?.error || err.message));
    } finally {
      setSendingEmail(false);
    }
  };

  useEffect(() => {
    if (data) {
      if (!isEditing) {
        setEditComingFrom(data.store?.name || data.comingFrom || data.pickup_store || "");
        setEditDestination(data.address_line1 || data.destination || data.destination_store || "");
        setEditBillTo(data.recipient_name || data.billing_party || data.billTo || "");
        setEditInvoiceNo(data.invoiceNo ? data.invoiceNo.split(',').map(s=>s.trim()) : [""]);
        setEditPoNumber(data.po_number || data.poNumber ? (data.po_number || data.poNumber).split(',').map(s=>s.trim()) : [""]);
        setEditProducts(data.job_products ? data.job_products.map(p => ({
          sku: p.product?.code || p.raw_line || "",
          description: p.product?.description || p.raw_line || "",
          quantity: p.quantity || 1
        })) : []);
      }
    }
  }, [data, isEditing]);

  const handleSave = async () => {
    try {
      // The API serves normalized_data as a JSON *string* (legacy rows are
      // double-encoded). Spreading a string yields {"0":"{","1":"\"",...} and
      // would wipe the row's parse_status/address/phone/instructions on save,
      // making the order read as "unidentified". Decode before spreading.
      let baseNd = data.normalized_data;
      for (let i = 0; i < 3 && typeof baseNd === "string"; i++) {
        try { baseNd = JSON.parse(baseNd); } catch { baseNd = null; break; }
      }
      if (!baseNd || typeof baseNd !== "object" || Array.isArray(baseNd)) baseNd = {};

      const updatedNormalizedData = {
        ...baseNd,
        comingFrom: editComingFrom,
        destination: editDestination,
        billTo: editBillTo,
        products: editProducts,
        invoiceNo: editInvoiceNo.filter(Boolean).join(", "),
        poNumber: editPoNumber.filter(Boolean).join(", ")
      };
      
      const payload = {
        normalized_data: updatedNormalizedData,
        bt_from: editComingFrom,
        pickup_store: editComingFrom,
        bt_to: editDestination,
        destination_store: editDestination,
        billing_party: editBillTo,
        line_items: editProducts,
        invoiceNo: editInvoiceNo.filter(Boolean).join(", "),
        po_number: editPoNumber.filter(Boolean).join(", ")
      };

      const res = await API.put(`/jobs/${encodeURIComponent(data.id)}`, payload);
      setDetails(res.data);
      setIsEditing(false);
    } catch (err) {
      alert("Failed to save changes: " + err.message);
    }
  };

  const addProductRow = () => {
    setEditProducts([...editProducts, { sku: "", description: "", quantity: 1 }]);
  };
  const removeProductRow = (index) => {
    const newP = [...editProducts];
    newP.splice(index, 1);
    setEditProducts(newP);
  };
  const updateProduct = (index, field, value) => {
    const newP = [...editProducts];
    newP[index][field] = value;
    setEditProducts(newP);
  };

  if (!data) return null;

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      return format(new Date(d), "dd/MM/yyyy HH:mm");
    } catch {
      return d;
    }
  };

  const getFileUrl = (pathStr) => {
    if (!pathStr) return "";
    const parts = pathStr.split(/[/\\]/);
    const filename = parts[parts.length - 1];
    
    let baseUrl;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (process.env.REACT_APP_API_URL) {
      baseUrl = process.env.REACT_APP_API_URL.replace('/v1', '/uploads');
    } else {
      baseUrl = isLocal ? `http://localhost:8080/uploads` : `/api/uploads`;
    }
    return `${baseUrl}/${filename}`;
  };

  // A plain <a href> to /uploads carries no Authorization header, so the API
  // answered every attachment click with "Authentication required". The files
  // stay behind auth on purpose (they hold customer names, addresses and phone
  // numbers) - so fetch through the signed-in client and open the blob. The
  // tab is opened BEFORE the await: browsers block window.open after an async
  // gap as a popup.
  const [openingFile, setOpeningFile] = useState("");
  const openWithAuth = async (pathStr) => {
    if (!pathStr) return;
    const filename = pathStr.split(/[/\\]/).pop();
    const tab = window.open("", "_blank");
    setOpeningFile(filename);
    try {
      const res = await API.get(`/${encodeURIComponent(filename)}`, {
        baseURL: apiBaseUrl().replace(/\/v1\/?$/, "/uploads"),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      if (tab) tab.location = url; else window.location.assign(url);
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (err) {
      if (tab) tab.close();
      alert(`Could not open ${filename}: ${err.response?.data?.detail || err.message}`);
    } finally {
      setOpeningFile("");
    }
  };

  const handleAssignRun = async (runId) => {
    if (!runId) return;
    setAssigningRun(true);
    try {
      await API.post(`/runs/${runId}/assign-job/${encodeURIComponent(data.id)}`);
      fetchOrderDetails();
    } catch (e) {
      alert("Failed to assign run");
    } finally {
      setAssigningRun(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-slate-400 hover:text-white transition-all text-xs uppercase tracking-wider font-semibold"
      >
        <ArrowLeft size={16} /> Back to Dashboard
      </button>

      {!canEdit && <ReadOnlyBanner />}

      <div className="metric-card bg-slate-950/40 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-8">
        <div className="flex flex-wrap justify-between items-start gap-4 border-b border-slate-800 pb-5">
          <div>
            <div className="flex items-center gap-2.5">
              {isEditing ? (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-xs text-slate-400 font-bold uppercase w-16">Invoices:</span>
                    {editInvoiceNo.map((inv, idx) => (
                      <input key={idx} value={inv} onChange={e => { const newInv = [...editInvoiceNo]; newInv[idx] = e.target.value; setEditInvoiceNo(newInv); }} placeholder="Invoice No" className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-white font-bold outline-none focus:border-indigo-500 w-28" />
                    ))}
                    <button onClick={() => setEditInvoiceNo([...editInvoiceNo, ""])} className="bg-slate-800 text-slate-300 px-2 py-1 rounded hover:bg-slate-700 hover:text-white text-xs font-bold transition-colors shadow-sm">+</button>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-xs text-slate-400 font-bold uppercase w-16">POs:</span>
                    {editPoNumber.map((po, idx) => (
                      <input key={idx} value={po} onChange={e => { const newPo = [...editPoNumber]; newPo[idx] = e.target.value; setEditPoNumber(newPo); }} placeholder="PO Number" className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-white font-bold outline-none focus:border-indigo-500 w-28" />
                    ))}
                    <button onClick={() => setEditPoNumber([...editPoNumber, ""])} className="bg-slate-800 text-slate-300 px-2 py-1 rounded hover:bg-slate-700 hover:text-white text-xs font-bold transition-colors shadow-sm">+</button>
                  </div>
                </div>
              ) : (
                <h2 className="text-2xl font-black tracking-tight text-white">
                  {data.order_number ? `Order ${data.order_number}` : (data.invoiceNo ? `Invoice ${data.invoiceNo}` : "Unidentified Document")}
                </h2>
              )}

              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
                data.status === "completed" ? "bg-green-500/10 text-green-400 border border-green-500/20" :
                data.status === "in_progress" ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" :
                "bg-slate-800 text-slate-400 border border-slate-700/30"
              }`}>
                {data.status}
              </span>
              {/* Extraction-quality chip — same language as the dashboard */}
              {data.parse_status && (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider border ${
                  data.parse_status === "ok" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" :
                  data.parse_status === "low_confidence" ? "bg-amber-500/10 text-amber-400 border-amber-500/20" :
                  "bg-rose-500/10 text-rose-400 border-rose-500/20"
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    data.parse_status === "ok" ? "bg-emerald-400" :
                    data.parse_status === "low_confidence" ? "bg-amber-400" : "bg-rose-400"}`}></span>
                  {data.parse_status === "ok" ? "OK" : data.parse_status === "low_confidence" ? "Low conf" : "Unidentified"}
                </span>
              )}
              
              {/* Re-extract, reprocess and Send Confirmation all change the
                  order (the last one emails the store), so the whole strip is
                  office work and is absent for a reader. */}
              {!canEdit ? null : !isEditing ? (
                <div className="ml-auto flex items-center gap-3">
                  <button
                    onClick={handleReparse}
                    disabled={isReparsing}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700 shadow-lg shadow-purple-600/20 text-sm font-bold transition-all disabled:opacity-50"
                  >
                    <RefreshCw size={16} className={isReparsing ? "animate-spin" : ""} /> {isReparsing ? "Extracting..." : "Re-Extract AI"}
                  </button>
                  <button
                    onClick={handleReprocess}
                    disabled={isReprocessing}
                    title="Re-run the parser and re-read the attachment (use after a clearer scan is re-sent)"
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-teal-600 text-white hover:bg-teal-700 shadow-lg shadow-teal-600/20 text-sm font-bold transition-all disabled:opacity-50"
                  >
                    <RefreshCw size={16} className={isReprocessing ? "animate-spin" : ""} /> {isReprocessing ? "Reprocessing..." : "Reprocess"}
                  </button>
                  <button
                    onClick={handleSendConfirmation}
                    disabled={sendingEmail}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/20 text-sm font-bold transition-all disabled:opacity-50"
                  >
                    <Mail size={16} /> {sendingEmail ? "Sending..." : "Send Confirmation"}
                  </button>
                  <button 
                    onClick={() => setIsEditing(true)} 
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 text-sm font-bold transition-all"
                  >
                    <Edit3 size={16} /> Edit Details
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 ml-auto">
                  <button 
                    onClick={handleSave}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700 border border-green-500 shadow-lg shadow-green-600/20 text-sm font-bold transition-colors"
                  >
                    <Save size={16} /> Save Changes
                  </button>
                  <button 
                    onClick={() => setIsEditing(false)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700 text-sm font-bold transition-colors"
                  >
                    <X size={16} /> Cancel
                  </button>
                </div>
              )}
            </div>
            <p className="text-slate-400 text-xs mt-1">
              {data.type && `Type: ${data.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`}
              {data.invoiceNo && data.invoiceNo !== "Not identified" && ` | Invoice: ${data.invoiceNo}`}
              {data.po_number && ` | PO: ${data.po_number}`}
              {data.additional_references && data.additional_references.length > 0 && ` | Addtl Refs: ${data.additional_references.join(", ")}`}
            </p>
          </div>
          {data.rate && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 px-4 py-2 rounded-xl text-right">
              <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Delivery Rate</p>
              <p className="text-lg font-black text-emerald-400 font-mono">${data.rate.toFixed(2)}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-slate-900/30 border border-slate-800/80 rounded-2xl p-5 space-y-4">
            <h3 className="font-bold text-white flex items-center gap-2 text-sm border-b border-slate-800 pb-2">
              <Package size={16} className="text-blue-400" /> Logistics Details
            </h3>
            <div className="grid grid-cols-2 gap-y-3 text-xs">
              <div>
                <p className="text-slate-500 uppercase tracking-wider font-semibold text-[10px]">BT Type</p>
                <p className="text-slate-200 mt-0.5 capitalize">{(data.bt_type === 'branch_transfer' || (data.bt_type && data.bt_type.toLowerCase().includes('goods'))) ? 'BT Branch Transfer' : data.bt_type === 'return_to_store' ? 'Return to Store' : data.bt_type === 'purchase_order' ? 'Purchase Order' : data.bt_type?.replace(/_/g, " ") || "Not identified"}</p>
              </div>
              <div>
                <p className="text-slate-500 uppercase tracking-wider font-semibold text-[10px]">Bill To</p>
                {isEditing ? <input value={editBillTo} onChange={e=>setEditBillTo(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-slate-200 text-xs mt-0.5 outline-none focus:border-indigo-500" /> : <p className="text-slate-200 mt-0.5">{data.billTo || "Not identified"}</p>}
              </div>
              <div>
                <p className="text-slate-500 uppercase tracking-wider font-semibold text-[10px]">BT Route Type</p>
                <p className="text-slate-200 mt-0.5">{data.bt_order_type || "Not identified"}</p>
              </div>
              <div>
                <p className="text-slate-500 uppercase tracking-wider font-semibold text-[10px]">BT Origin / Store</p>
                {isEditing ? <input value={editComingFrom} onChange={e=>setEditComingFrom(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-slate-200 text-xs mt-0.5 outline-none focus:border-indigo-500" /> : <p className="text-slate-200 mt-0.5">{data.comingFrom || "Not identified"}</p>}
              </div>
              <div>
                <p className="text-slate-500 uppercase tracking-wider font-semibold text-[10px]">BT Destination</p>
                {isEditing ? <input value={editDestination} onChange={e=>setEditDestination(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-slate-200 text-xs mt-0.5 outline-none focus:border-indigo-500" /> : <p className="text-slate-200 mt-0.5">{data.destination || "Not identified"}</p>}
              </div>
              <div className="col-span-2">
                <p className="text-slate-500 uppercase tracking-wider font-semibold text-[10px]">Delivery Route Location</p>
                <p className="text-slate-200 mt-0.5 font-medium">{data.destination || "Not identified"}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-900/30 border border-slate-800/80 rounded-2xl p-5 space-y-4">
            <h3 className="font-bold text-white flex items-center gap-2 text-sm border-b border-slate-800 pb-2">
              <MapPin size={16} className="text-orange-400" /> Delivery & Customer Info
            </h3>
            <div className="grid grid-cols-2 gap-y-3 text-xs">
              <div>
                <p className="text-slate-500 uppercase tracking-wider font-semibold text-[10px]">Customer Name</p>
                <p className="text-slate-200 mt-0.5 font-bold">{data.recipient_name || data.customer_name || "Not identified"}</p>
              </div>
              <div>
                <p className="text-slate-500 uppercase tracking-wider font-semibold text-[10px]">Contact Phone</p>
                <p className="text-slate-200 mt-0.5 font-medium">{data.recipient_phone || data.customer_phone || "Not identified"}</p>
              </div>
              <div className="col-span-2">
                <p className="text-slate-500 uppercase tracking-wider font-semibold text-[10px]">Exact Address / Destination</p>
                {/* serialize_order overwrites `destination` with the fuzzy-matched
                    STORE name, so a customer delivery showed "Customer (Direct)"
                    under a field labelled "Exact Address" while the real street
                    address sat unused in the payload. Prefer the address. */}
                <p className="text-slate-200 mt-0.5 group flex items-start gap-2">
                  <span>
                    {data.address_line || data.destination_address || data.destination ||
                      <span className="text-slate-600">—</span>}
                  </span>
                  {/* Edits go through PATCH /stop-data {address_line} - the safe
                      path. Feeding the editable destination field instead would
                      write the street into destination_store, where it fuzzy-
                      matches to the wrong store. */}
                  {canEdit && (
                    <button
                      className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-blue-400 text-[11px] underline"
                      onClick={async () => {
                        const cur = data.address_line || data.destination_address || "";
                        const v = window.prompt("Delivery street address:", cur);
                        if (v === null || v.trim() === cur.trim()) return;
                        try {
                          await API.patch(`/jobs/${encodeURIComponent(data.id)}/stop-data`,
                                          { address_line: v.trim() });
                          await fetchOrderDetails();
                        } catch (err) {
                          alert("Failed to save address: " + (err.response?.data?.detail || err.message));
                        }
                      }}>
                      edit
                    </button>
                  )}
                </p>
                {(data.address_line || data.destination_address) && data.destination &&
                  data.destination !== (data.address_line || data.destination_address) && (
                  <p className="text-slate-500 text-[11px] mt-0.5">{data.destination}</p>
                )}
              </div>
              <div>
                <p className="text-slate-500 uppercase tracking-wider font-semibold text-[10px]">Preferred Delivery Date</p>
                <p className="text-slate-200 mt-0.5">{data.preferred_delivery_date || "Not identified"}</p>
              </div>
              <div className="flex items-center gap-4 col-span-2 mt-1 bg-slate-950/40 p-2 rounded-lg border border-slate-800/60">
                <span className="flex items-center gap-1">
                  <span className={`w-2.5 h-2.5 rounded-full ${data.requires_assembly ? "bg-blue-500" : "bg-slate-800"}`}></span>
                  <span className="text-[10px] text-slate-400">Assembly: {data.requires_assembly ? "Yes" : "No"}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className={`w-2.5 h-2.5 rounded-full ${data.has_rubbish_removal ? "bg-green-500" : "bg-slate-800"}`}></span>
                  <span className="text-[10px] text-slate-400">Rubbish: {data.has_rubbish_removal ? "Yes" : "No"}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-900/20 border border-slate-800/60 rounded-2xl p-5 space-y-4">
          <h3 className="font-bold text-white flex items-center gap-2 text-sm">
            <Clock size={16} className="text-indigo-400" /> Delivery Status Tracking
          </h3>
          <div className="flex flex-wrap gap-8 justify-around p-2">
            {[
              { label: "Picked Up", done: data.picked_up, date: data.picked_up_at },
              { label: "Delivered", done: data.delivered, date: data.delivered_at },
              { label: "Billed", done: data.billed, date: data.billed_at },
            ].map((st, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className={`p-2 rounded-full border ${st.done ? "bg-green-500/10 border-green-500/20 text-green-400" : "bg-slate-900 border-slate-800 text-slate-600"}`}>
                  {st.done ? <CheckCircle size={22} className="drop-shadow-[0_0_4px_rgba(34,197,94,0.2)]" /> : <Circle size={22} />}
                </div>
                <div className="text-xs">
                  <p className={`font-bold ${st.done ? "text-slate-100" : "text-slate-500"}`}>{st.label}</p>
                  {st.done && st.date && <p className="text-[10px] text-slate-400 mt-0.5">{formatDate(st.date)}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-900/20 border border-slate-800/60 rounded-2xl p-5">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-1">
                <Truck className="text-emerald-400" size={20} />
                Dispatch & Run Assignment
              </h3>
              <p className="text-sm text-slate-400">Assign this order to a daily run sheet.</p>
            </div>
            {data.run_id && (
              <div className="bg-emerald-500/10 text-emerald-400 px-3 py-1 rounded border border-emerald-500/20 text-sm font-medium">
                Assigned to Run #{data.run_id}
              </div>
            )}
          </div>
          
          <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
            <label className="block text-sm font-medium text-slate-400 mb-2">
              {canEdit ? "Assign to Run" : "Run"}
            </label>
            {canEdit ? (
              <div className="flex gap-2">
                <select
                  className="flex-1 bg-slate-800 border border-slate-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                  onChange={(e) => handleAssignRun(e.target.value)}
                  value={data.run_id || ""}
                  disabled={assigningRun}
                >
                  <option value="">Select a run...</option>
                  {runs.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.name || `Run #${r.id}`} ({r.date}) - {r.driver_id ? `Driver ${r.driver_id}` : "No Driver"}
                    </option>
                  ))}
                </select>
                {assigningRun && <span className="text-slate-400 text-sm py-2">Assigning...</span>}
              </div>
            ) : (
              /* Which run this stop is on is worth reading; putting it on one
                 is dispatch work. Show the answer, not a dead picker. */
              <p className="text-slate-200 text-sm">
                {data.run_id
                  ? (() => {
                      const r = runs.find((x) => String(x.id) === String(data.run_id));
                      return r ? `${r.name || `Run #${r.id}`} (${r.date})` : `Run #${data.run_id}`;
                    })()
                  : "Not assigned to a run yet."}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-slate-900/30 border border-slate-800/80 rounded-2xl p-5 space-y-4">
            <h3 className="font-bold text-white flex items-center gap-2 text-sm border-b border-slate-800 pb-2">
              <Mail size={16} className="text-blue-400" /> Original Email Source
            </h3>
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-1 text-slate-500">From:</div>
                <div className="col-span-2 text-slate-300 font-medium">{data.email_from || "Not identified"}</div>
                
                <div className="col-span-1 text-slate-500">Subject:</div>
                <div className="col-span-2 text-slate-300 font-semibold">{data.notes || data.sourceEmailSubject || "Not identified"}</div>
                
                <div className="col-span-1 text-slate-500">Date Received:</div>
                <div className="col-span-2 text-slate-400">{formatDate(data.email_date)}</div>

                <div className="col-span-1 text-slate-500">Docling Confidence:</div>
                <div className="col-span-2">
                  <span className={`px-2 py-0.5 rounded font-mono text-[10px] font-bold ${
                    data.confidence >= 0.8 ? "bg-green-500/10 text-green-400" :
                    data.confidence >= 0.5 ? "bg-amber-500/10 text-amber-400" :
                    "bg-red-500/10 text-red-400"
                  }`}>
                    {(data.confidence * 100).toFixed(0)}%
                  </span>
                </div>
              </div>

              {data.email_screenshot_path && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => openWithAuth(data.email_screenshot_path)}
                    disabled={!!openingFile}
                    className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 hover:underline font-bold disabled:opacity-60"
                  >
                    <FileText size={14} /> Open Saved Email Screenshot (.eml)
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Delivery Instructions */}
          <div className="bg-slate-900/30 border border-slate-800/80 rounded-2xl p-5 space-y-3">
            <h3 className="font-bold text-white flex items-center gap-2 text-sm border-b border-slate-800 pb-2">
              <FileText size={16} className="text-violet-400" /> Delivery Instructions
            </h3>
            <p className="text-xs text-slate-300 leading-relaxed italic bg-slate-950/40 p-3 rounded-xl border border-slate-900">
              {data.notes || data.delivery_instructions || "No special instructions provided."}
            </p>
          </div>
        </div>

        {/* Product Items Table */}
        <div className="bg-slate-900/30 border border-slate-800/80 rounded-2xl p-5 space-y-4">
            <h3 className="font-bold text-white flex items-center gap-2 text-sm">
              <Package size={16} className="text-emerald-400" /> Items List ({isEditing ? editProducts.length : (data.job_products?.length || data.products?.length || 0)})
            </h3>
            <div className="border border-slate-800 rounded-xl overflow-hidden bg-slate-950/20 overflow-x-auto">
              <table className="w-full text-xs text-left text-slate-300">
                <thead className="bg-slate-950/50 text-slate-400 uppercase text-[10px] tracking-wider border-b border-slate-800">
                  <tr>
                    <th className="px-4 py-3 font-semibold">SKU / Code</th>
                    <th className="px-4 py-3 font-semibold">Description</th>
                    <th className="px-4 py-3 font-semibold w-24">Quantity</th>
                    {isEditing && <th className="px-4 py-3 font-semibold text-center w-16">Action</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/40">
                  {isEditing ? (
                    editProducts.map((item, i) => (
                      <tr key={i} className="hover:bg-slate-900/10">
                        <td className="px-4 py-2">
                          <input value={item.sku} onChange={e => updateProduct(i, 'sku', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-blue-400 font-mono font-bold outline-none focus:border-indigo-500" placeholder="SKU" />
                        </td>
                        <td className="px-4 py-2">
                          <input value={item.description} onChange={e => updateProduct(i, 'description', e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-slate-300 outline-none focus:border-indigo-500" placeholder="Description" />
                        </td>
                        <td className="px-4 py-2">
                          <input type="number" value={item.quantity} onChange={e => updateProduct(i, 'quantity', e.target.value)} className="w-16 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-white font-mono outline-none focus:border-indigo-500" min="1" />
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button onClick={() => removeProductRow(i)} className="text-slate-500 hover:text-red-400 p-1 transition-colors"><Trash2 size={16} /></button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    (data.job_products || data.products || []).length > 0 ? (
                      (data.job_products || data.products).map((item, i) => (
                        <tr key={i} className="hover:bg-slate-900/10">
                          <td className="px-4 py-3 font-mono font-bold text-blue-400">{cleanText(item.product?.code || item.sku || item.raw_line)}</td>
                          <td className="px-4 py-3 text-slate-300">{cleanText(item.product?.description || item.description || item.raw_line) || "Not identified"}</td>
                          <td className="px-4 py-3 text-white font-mono">{item.quantity}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="px-4 py-8 text-center text-slate-500 italic">
                          No products identified. Click "Edit" to add products manually.
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
            {isEditing && (
              <button onClick={addProductRow} className="flex items-center gap-1 text-xs font-bold text-emerald-400 hover:text-emerald-300 transition-colors uppercase tracking-wider bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 px-3 py-1.5 rounded-lg w-fit mt-3">
                <Plus size={14} /> Add Product
              </button>
            )}
          </div>

        {/* Email Attachments Links */}
        {data.attachments && data.attachments.length > 0 && (
          <div className="bg-slate-900/20 border border-slate-800/60 rounded-2xl p-5 space-y-3">
            <h3 className="font-bold text-white flex items-center gap-2 text-sm">
              <ImageIcon size={16} className="text-amber-400" /> Email Attachment Files
            </h3>
            <div className="flex flex-wrap gap-2">
              {data.attachments.map((att) => (
                <button
                  type="button"
                  key={att.id}
                  onClick={() => openWithAuth(att.file_path)}
                  disabled={!!openingFile}
                  title={att.filename}
                  className="bg-slate-900 hover:bg-slate-800 border border-slate-700/80 px-3.5 py-2.5 rounded-xl text-xs text-slate-200 hover:text-white font-semibold transition-all flex items-center gap-1.5 shadow-sm disabled:opacity-60"
                >
                  <FileText size={14} className="text-amber-400" />
                  {openingFile && att.file_path && att.file_path.endsWith(openingFile) ? "Opening…" : att.filename}
                </button>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
